import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import PageHero, { Breadcrumb } from "@/components/feature/PageHero";
import { useSeo } from "@/hooks/useSeo";
import { COMPANY_INTRO, COMPANY_NAME, COMPANY_TAGLINE, companyStats, contentScopes, milestones } from "@/mocks/company";
import ContactForm from "@/pages/about/components/ContactForm";

const FORM_ID = "xingguang-contact-form";
const FORM_NAME = "星光传媒联系我们";
const FORM_SUBMIT_URL = "https://readdy.ai/api/form/dbadvj69if4sq287d150";

export default function About() {
  useSeo({
    title: "关于我们 - 星光传媒",
    description:
      "星光传媒成立于 2014 年，是一家专注电影与电视剧推荐及影视资讯的内容机构。了解我们的发展历程、内容范围、线下放映网络与合作联系方式。",
    keywords: "星光传媒,关于星光传媒,影视内容机构,影视资讯团队,影视制作",
    canonicalPath: "/about",
  });

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar currentLabel="关于" />
      <main>
        <PageHero
          eyebrow="About Starlight"
          title="关于星光传媒"
          description={`${COMPANY_TAGLINE}。我们从一间 28 座的小放映室出发，如今希望成为你每次“今晚看点什么”时第一个想起的地方。`}
          background="https://readdy.ai/api/search-image?query=Cinematic%20wide%20shot%20of%20a%20small%20art%20house%20screening%20room%20with%20deep%20navy%20velvet%20seats%20and%20a%20warm%20amber%20projector%20beam%2C%20minimal%20elegant%20atmospheric%20composition%2C%20high%20detail&width=1600&height=900&seq=about-hero-bg&orientation=landscape"
        />

        <div className="px-4 md:px-6 py-5 border-b border-background-200">
          <div className="max-w-[1440px] mx-auto">
            <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />
          </div>
        </div>

        <section className="px-4 md:px-6 py-12 md:py-16">
          <div className="max-w-[1440px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-12 items-center">
            <div className="lg:col-span-7">
              <span className="text-xs tracking-[0.22em] uppercase text-secondary-600">Who we are</span>
              <h2 className="font-heading text-2xl md:text-4xl font-semibold text-foreground-950 mt-2 mb-4 leading-tight">
                我们是一个喜欢把电影
                <br className="hidden md:block" />
                认真看完的团队
              </h2>
              <p className="text-sm md:text-base text-foreground-700 leading-8 mb-4">{COMPANY_INTRO}</p>
              <p className="text-sm md:text-base text-foreground-700 leading-8 mb-6">
                我们不做“最快的最新消息”，只想做“最值得你花两个小时的推荐”。{COMPANY_NAME}
                的内容团队由编辑、影评人、影像研究者与活动策划组成，每一部作品都经过初筛、解读和观众反馈三轮评估，才会出现在星光片单里。
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {companyStats.map((stat) => (
                  <div key={stat.label} className="bg-background-100 rounded-lg px-3 py-3.5">
                    <p className="font-heading text-xl md:text-2xl font-semibold text-primary-700 leading-none">{stat.value}</p>
                    <p className="text-xs text-foreground-800 mt-2 leading-snug">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="w-full aspect-[4/5] rounded-lg overflow-hidden bg-background-200">
                <img
                  src="https://readdy.ai/api/search-image?query=Cinematic%20editorial%20photo%20of%20a%20small%20film%20editorial%20team%20in%20a%20warm%20library%20with%20shelves%20of%20film%20books%20and%20a%20projector%2C%20soft%20amber%20light%2C%20deep%20navy%20shadows%2C%20documentary%20style%2C%20high%20detail&width=900&height=1125&seq=about-team-photo&orientation=portrait"
                  alt="星光传媒内容团队工作场景"
                  title="星光传媒内容团队"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="bg-background-100 px-4 md:px-6 py-12 md:py-16">
          <div className="max-w-[1440px] mx-auto">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
              <div>
                <span className="text-xs tracking-[0.22em] uppercase text-secondary-600">Milestones</span>
                <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mt-2">发展历程</h2>
              </div>
              <p className="text-sm text-foreground-500 max-w-md">十二年，我们只做了一件事：把好故事介绍给更多人。</p>
            </div>
            <div className="relative">
              <div className="hidden md:block absolute left-0 right-0 top-[18px] h-px bg-background-300"></div>
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-6 md:gap-4">
                {milestones.map((item, index) => (
                  <div key={item.year} className="relative">
                    <div className="flex items-center gap-2 mb-3">
                      <span
                        className={`relative z-10 w-2.5 h-2.5 rounded-full ${
                          index === milestones.length - 1 ? "bg-accent-500" : "bg-primary-500"
                        }`}
                      ></span>
                      <span className="font-heading text-lg font-semibold text-foreground-950">{item.year}</span>
                    </div>
                    <h3 className="text-sm font-medium text-foreground-900 mb-1.5">{item.title}</h3>
                    <p className="text-xs text-foreground-600 leading-relaxed">{item.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="px-4 md:px-6 py-12 md:py-16">
          <div className="max-w-[1440px] mx-auto">
            <div className="mb-8">
              <span className="text-xs tracking-[0.22em] uppercase text-secondary-600">What we cover</span>
              <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mt-2">内容范围</h2>
              <p className="text-sm text-foreground-500 mt-2 max-w-2xl">
                四条内容线互为补充：既有作品推荐，也有资讯追踪与线下交流，让一部作品从首映到被记住，都有完整记录。
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
              {contentScopes.map((scope, index) => (
                <div key={scope.title} className="bg-background-100 rounded-lg p-5 flex flex-col">
                  <span
                    className={`w-10 h-10 rounded-md flex items-center justify-center mb-4 ${
                      index === 1 ? "bg-accent-100 text-accent-800" : "bg-primary-100 text-primary-700"
                    }`}
                  >
                    <i className={`${scope.icon} text-lg w-5 h-5 flex items-center justify-center`}></i>
                  </span>
                  <h3 className="text-sm md:text-base font-medium text-foreground-950 mb-2">{scope.title}</h3>
                  <p className="text-xs md:text-sm text-foreground-600 leading-relaxed flex-1">{scope.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-background-100 px-4 md:px-6 py-12 md:py-16">
          <div className="max-w-[1440px] mx-auto grid grid-cols-1 lg:grid-cols-[1fr_1.1fr] gap-8 md:gap-12">
            <div>
              <span className="text-xs tracking-[0.22em] uppercase text-secondary-600">Get in touch</span>
              <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mt-2 mb-3">合作与联系</h2>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-6">
                无论你是创作者、片方、影院合作方，还是只是想给我们推荐一部喜欢的作品，都欢迎留言。
              </p>
              <div className="space-y-4">
                {[
                  { icon: "ri-mail-line", label: "合作邮箱", value: "partner@starlight-media.example" },
                  { icon: "ri-map-pin-line", label: "办公地址", value: "上海市静安区星光路 12 号 光影大厦 8 层" },
                  { icon: "ri-time-line", label: "工作时间", value: "周一至周五 10:00 - 19:00" },
                  { icon: "ri-ticket-2-line", label: "放映活动", value: "每月第一个周六 · 主题影展与主创交流" },
                ].map((item) => (
                  <div key={item.label} className="flex items-start gap-3 bg-background-50 rounded-lg p-3.5">
                    <span className="w-9 h-9 rounded-md bg-primary-100 text-primary-700 flex items-center justify-center shrink-0">
                      <i className={`${item.icon} text-base w-4 h-4 flex items-center justify-center`}></i>
                    </span>
                    <div>
                      <p className="text-xs text-foreground-400">{item.label}</p>
                      <p className="text-sm text-foreground-900 mt-0.5">{item.value}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-6 bg-secondary-100 rounded-lg p-4">
                <p className="text-xs text-secondary-900/80 leading-relaxed">
                  本页面联系信息为演示内容，仅用于展示网站结构。表单提交将真实记录到星光传媒留言表单中。
                </p>
              </div>
            </div>
            <ContactForm formId={FORM_ID} submitUrl={FORM_SUBMIT_URL} formName={FORM_NAME} />
          </div>
        </section>

        <section className="px-4 md:px-6 py-12 md:py-14">
          <div className="max-w-[1440px] mx-auto grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
            {[
              {
                title: "版权声明",
                content:
                  "本站所展示的影视作品名称、剧情简介、演职人员与评分为虚构演示内容，仅用于网站功能展示。如涉及真实作品，其版权归原权利人所有。",
              },
              {
                title: "使用须知",
                content:
                  "本站不提供任何影视作品的在线播放、下载或资源分发服务。站内所有搜索结果、收藏与分享功能均为前端演示，不构成商业承诺。",
              },
              {
                title: "内容准则",
                content:
                  "星光编辑部坚持尊重创作者、尊重观众的原则。评论与推荐不收取费用，所有评分与榜单均基于编辑评估与观众反馈综合形成。",
              },
            ].map((item) => (
              <div key={item.title} className="bg-background-100 rounded-lg p-5">
                <h3 className="text-sm font-medium text-foreground-950 mb-2 flex items-center gap-1.5">
                  <i className="ri-shield-check-line text-accent-600 w-4 h-4 flex items-center justify-center"></i>
                  {item.title}
                </h3>
                <p className="text-xs text-foreground-600 leading-relaxed">{item.content}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="px-4 md:px-6 pb-14 md:pb-16">
          <div className="max-w-[1440px] mx-auto bg-primary-950 rounded-lg px-6 py-10 md:px-10 md:py-12 flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div>
              <p className="text-xs tracking-[0.22em] uppercase text-accent-300 mb-2">Starlight Picks</p>
              <h2 className="font-heading text-xl md:text-2xl font-semibold text-background-50 mb-2">今晚看点什么？</h2>
              <p className="text-sm text-background-300/80 max-w-lg">从最新上线的作品开始，或者按题材慢慢挑。</p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/category"
                className="h-11 px-5 inline-flex items-center rounded-md bg-accent-500 text-primary-950 text-sm font-medium hover:bg-accent-400 transition-colors cursor-pointer whitespace-nowrap"
              >
                浏览影视分类
              </Link>
              <Link
                to="/news"
                className="h-11 px-5 inline-flex items-center rounded-md border border-background-50/25 text-background-100 text-sm hover:bg-background-50/10 transition-colors cursor-pointer whitespace-nowrap"
              >
                阅读影视资讯
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}